import React, {useEffect, useState} from 'react'
import {DataTable} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {API_URL} from '../../../modules/auth/core/_requests' // adjust path as needed

export type InventoryItem = {
  sku: string
  asin: string
  sellable: number
  inbound: number
  damaged: number
  other: number
  total: number
  sales_velocity: number
  days_of_sales: string
  product: {
    title: string
    image: string
  }
  store: 'ec' | 'vintage'
}

type PaginationInfo = {
  total: number
  current_page: number
  per_page: number
  pages: number
}

const AmazonInventoryWrapper: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    current_page: 1,
    per_page: 25,
    pages: 0,
  })
  const [loading, setLoading] = useState<boolean>(false)

  // For sorting, use explicit type 1 or -1.
  const [sortField, setSortField] = useState<string>('sellable')
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1)

  // Store filter: "both", "ec", or "vintage"
  const [storeFilter, setStoreFilter] = useState<'both' | 'ec' | 'vintage'>('both')

  /**
   * Fetch inventory data from our backend.
   */
  const fetchInventory = async (params?: {
    page?: number
    items_per_page?: number
    sort_field?: string
    sort_order?: string
    store?: string
  }) => {
    setLoading(true)
    try {
      const page = params?.page || pagination.current_page
      const items_per_page = params?.items_per_page || pagination.per_page
      const sort_field = params?.sort_field || sortField
      const sort_order = params?.sort_order || (sortOrder === 1 ? 'asc' : 'desc')
      const store = params?.store || storeFilter
      const query = new URLSearchParams({
        page: page.toString(),
        items_per_page: items_per_page.toString(),
        sort_field,
        sort_order,
        store,
      })
      const resp = await fetch(`${API_URL}/inventory?${query.toString()}`)
      const json = await resp.json()
      setInventory(json.data)
      setPagination(json.pagination)
    } catch (error) {
      console.error('Error fetching inventory', error)
    } finally {
      setLoading(false)
    }
  }

  // Initial load and whenever sort or store filter changes.
  useEffect(() => {
    fetchInventory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, storeFilter])

  /**
   * Handler for page changes.
   */
  const onPageChange = (event: any) => {
    // event.page is zero-based; add 1 for our API.
    const page = event.page + 1
    fetchInventory({
      page,
      items_per_page: event.rows,
      sort_field: sortField,
      sort_order: sortOrder === 1 ? 'asc' : 'desc',
      store: storeFilter,
    })
  }

  /**
   * Handler for sort changes.
   */
  const onSortChange = (event: any) => {
    const newSortField = event.sortField
    const newSortOrder = event.sortOrder // expected to be 1 or -1
    setSortField(newSortField)
    setSortOrder(newSortOrder)
    // Reset to first page on sort change.
    fetchInventory({
      page: 1,
      items_per_page: pagination.per_page,
      sort_field: newSortField,
      sort_order: newSortOrder === 1 ? 'asc' : 'desc',
      store: storeFilter,
    })
  }

  /**
   * Template for the product image column.
   */
  const imageBodyTemplate = (rowData: InventoryItem) => {
    return (
      <img
        src={rowData.product.image}
        alt={rowData.product.title}
        style={{width: '50px', height: '50px'}}
      />
    )
  }

  /**
   * Template for the product title column.
   */
  const titleBodyTemplate = (rowData: InventoryItem) => {
    return rowData.product.title
  }

  return (
    <div className='card'>
      <h2>Amazon Inventory</h2>
      {/* Store filter dropdown */}
      <div style={{marginBottom: '1rem'}}>
        <label>Store: </label>
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value as 'both' | 'ec' | 'vintage')}
        >
          <option value='both'>Both</option>
          <option value='ec'>EC</option>
          <option value='vintage'>Vintage</option>
        </select>
      </div>
      <DataTable
        value={inventory}
        lazy
        paginator
        rows={pagination.per_page}
        totalRecords={pagination.total}
        first={(pagination.current_page - 1) * pagination.per_page}
        loading={loading}
        onPage={onPageChange}
        onSort={onSortChange}
        sortField={sortField}
        sortOrder={sortOrder}
        dataKey='sku'
      >
        <Column field='sku' header='SKU' sortable />
        <Column field='asin' header='ASIN' sortable />
        <Column field='sellable' header='Sellable' sortable />
        <Column field='inbound' header='Inbound' sortable />
        <Column field='damaged' header='Damaged' sortable />
        <Column field='other' header='Other' sortable />
        <Column field='total' header='Total' sortable />
        <Column field='sales_velocity' header='Sales Velocity' sortable />
        <Column field='days_of_sales' header='Days of Sales' sortable />
        <Column header='Title' body={titleBodyTemplate} sortable sortField='product.title' />
        <Column header='Image' body={imageBodyTemplate} />
        <Column field='store' header='Store' sortable />
      </DataTable>
    </div>
  )
}

export default AmazonInventoryWrapper
